import { Link } from 'react-router-dom';
import { movies, hotMovies } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import SEOHead from '@/components/feature/SEOHead';
import HeroSection from './components/HeroSection';
import CategoryNav from './components/CategoryNav';
import HotMovies from './components/HotMovies';
import LatestMovies from './components/LatestMovies';
import FAQSection from './components/FAQSection';

function StatsSection() {
  const stats = [
    { value: '20,000+', label: '电影资源' },
    { value: '8', label: '精细分类' },
    { value: '500万+', label: '月活用户' },
    { value: '99.9%', label: '可用率' },
  ];

  return (
    <section className="py-10 md:py-14 bg-background-100/50">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-2xl md:text-3xl font-heading font-bold text-accent-400 mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-foreground-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="py-12 md:py-16 relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Cinematic%20movie%20theater%20interior%20with%20empty%20red%20seats%2C%20dramatic%20lighting%2C%20dark%20atmosphere%2C%20warm%20golden%20accents%2C%20luxurious%20texture%2C%20epic%20scale%2C%20movie%20experience&width=1600&height=500&seq=cta-bg-home&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-background-50/80"></div>
      </div>
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-6 text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-100 mb-3">
          准备好开启你的观影之旅了吗？
        </h2>
        <p className="text-foreground-500 text-sm md:text-base mb-6 max-w-lg mx-auto">
          海量高清电影资源，八大精细分类，免费在线观看。随时随地，畅享精彩电影世界。
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Link
            to="/category/action"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-movie-line"></i>
            开始观影
          </Link>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-foreground-300/30 text-foreground-200 text-sm font-medium hover:bg-foreground-100/10 transition-colors cursor-pointer whitespace-nowrap"
          >
            了解更多
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function SidebarSection() {
  return (
    <section className="py-8 md:py-10">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 mb-5">
              精选推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {movies.slice(8, 14).map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 mb-5">
              排行榜
            </h2>
            <div className="bg-background-100 rounded-lg border border-background-200/50 p-4 space-y-2">
              {hotMovies.slice(0, 8).map((movie, index) => (
                <Link
                  key={movie.id}
                  to={`/movie/${movie.id}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-200/50 transition-colors cursor-pointer group"
                >
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold flex-shrink-0 ${
                      index < 3
                        ? 'bg-accent-500 text-white'
                        : 'bg-background-200 text-foreground-500'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-200 group-hover:text-primary-400 transition-colors truncate">
                      {movie.title}
                    </p>
                    <p className="text-xs text-foreground-500">
                      {movie.year} · {movie.category}
                    </p>
                  </div>
                  <span className="text-xs text-primary-400 font-medium flex-shrink-0">
                    {movie.rating}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const websiteSchema = {
    '@type': 'WebSite',
    name: '星空电影网',
    url: 'https://www.xingkongmovie.com',
    description: '星空电影网电影大全涵盖最新电影、热门电影、动作片、喜剧片等丰富分类，高清免费在线观看',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.xingkongmovie.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEOHead
        title="星空电影网 电影大全高清免费在线看 | 最新电影热门电影动作片喜剧片分类推荐"
        description="星空电影网电影大全涵盖最新电影、热门电影、动作片、喜剧片等丰富分类，高清免费在线观看。分类清晰片单齐全，找片看片一站搞定，畅享高清观影体验。"
        keywords="星空电影网,电影大全,最新电影,热门电影,动作片,喜剧片,高清电影,免费在线观看,电影分类,在线看电影"
        canonicalUrl="https://www.xingkongmovie.com/"
        schemaData={websiteSchema}
      />

      <main>
        <HeroSection />
        <CategoryNav />
        <HotMovies />
        <StatsSection />
        <LatestMovies />
        <SidebarSection />
        <CtaSection />
        <FAQSection />
      </main>
    </>
  );
}